import { trackConfirmedGa4Purchase } from '../../lib/api';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { getMarketplaceOrdersFn } from '../../lib/api';
import { getMarketplaceCartController } from '../../lib/marketplaceCart';
import config from '../../lib/config';
import { getCookie } from '../../lib/utils';
import '../../styles/layout.scss';
import './MarketplaceSuccess.scss';

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const MARKETPLACE_DATE_FORMAT_OPTIONS = {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
};

const STATUS_CONFIG = {
  success: {
    tone: 'success',
    eyebrow: 'Checkout complete',
    title: 'Purchase complete',
    description: 'Your marketplace checkout was completed successfully. Your purchase should now be available in your account.',
    badgeLabel: 'Success'
  },
  pending: {
    tone: 'pending',
    eyebrow: 'Payment in progress',
    title: 'Purchase pending',
    description: 'Your payment is still being processed. Give it a moment and then check your account for the latest order status.',
    badgeLabel: 'Pending'
  },
  failed: {
    tone: 'failed',
    eyebrow: 'Payment issue',
    title: 'Purchase not completed',
    description: 'We could not confirm this checkout. You can try again or review your account for the latest payment status.',
    badgeLabel: 'Failed'
  },
  unknown: {
    tone: 'unknown',
    eyebrow: 'Checkout return',
    title: 'We are checking your purchase',
    description: 'We received your return from checkout, but the final status is not available yet. You can review the order details below or check your account.',
    badgeLabel: 'Unknown'
  }
};

const normalizeToken = (value) => String(value ?? '').trim().toLowerCase();

const parsePositiveInteger = (value) => {
  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
    return null;
  }
  return parsedValue;
};

const normalizeQueryStatus = (value) => {
  const normalizedValue = normalizeToken(value);
  if (normalizedValue === 'success') {
    return 'success';
  }
  if (normalizedValue === 'pending') {
    return 'pending';
  }
  if (normalizedValue === 'failed' || normalizedValue === 'error') {
    return 'failed';
  }
  return null;
};

const normalizeOrderStatus = (value) => {
  const normalizedValue = normalizeToken(value);
  if (['success', 'paid', 'completed', 'complete', 'fulfilled'].includes(normalizedValue)) {
    return 'success';
  }
  if (['pending', 'processing', 'open', 'created', 'requires_action'].includes(normalizedValue)) {
    return 'pending';
  }
  if (['failed', 'error', 'cancelled', 'canceled', 'declined', 'expired'].includes(normalizedValue)) {
    return 'failed';
  }
  return null;
};

const formatMarketplacePrice = (value, currency = 'USD') => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return '$0.00';
  }

  const normalizedCurrency = String(currency || 'USD').trim().toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parsedValue);
  } catch (error) {
    return PRICE_FORMATTER.format(parsedValue);
  }
};

const formatMarketplaceDateTime = (value, fallback = 'Pending') => {
  if (!value) {
    return fallback;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return fallback;
  }

  return parsedDate.toLocaleString('en-US', MARKETPLACE_DATE_FORMAT_OPTIONS);
};

const getMarketplaceOrderStatusLabel = (value) => {
  const normalizedValue = normalizeToken(value);
  if (!normalizedValue) {
    return 'Unknown';
  }
  return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};

const getMarketplaceOrderAssetItems = (order) => {
  if (!Array.isArray(order?.items)) {
    return [];
  }

  return order.items.filter((item) => item?.line_type === 'asset');
};

const StatusIcon = ({ tone }) => {
  if (tone === 'success') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 6L9 17L4 12" />
      </svg>
    );
  }

  if (tone === 'pending') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 6V12L16 14" />
        <circle cx="12" cy="12" r="8" />
      </svg>
    );
  }

  if (tone === 'failed') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 7L17 17" />
        <path d="M17 7L7 17" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 10V14" />
      <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
};

const MarketplaceSuccess = () => {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const cartClearedRef = useRef(false);

  const orderId = useMemo(
    () => parsePositiveInteger(searchParams.get('order_id') ?? searchParams.get('orderId')),
    [searchParams]
  );
  const queryStatus = useMemo(
    () => normalizeQueryStatus(searchParams.get('status')),
    [searchParams]
  );
  const hasSessionToken = useMemo(
    () => Boolean(getCookie(config.sessionTokenCookieKey)),
    []
  );

  useEffect(() => {
    if (!orderId || !hasSessionToken) {
      setOrder(null);
      setIsOrderLoading(false);
      setOrderError(null);
      return;
    }

    let isCancelled = false;
    setIsOrderLoading(true);
    setOrderError(null);

    getMarketplaceOrdersFn()
      .then((orders) => {
        if (isCancelled) {
          return;
        }

        const matchedOrder = (Array.isArray(orders) ? orders : []).find(
          (candidate) => Number(candidate?.id) === orderId
        );
        setOrder(matchedOrder || null);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        console.log('Error while loading marketplace success order details', error);
        setOrder(null);
        setOrderError('Unable to load the full order details right now.');
      })
      .finally(() => {
        if (!isCancelled) {
          setIsOrderLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [hasSessionToken, orderId]);

  const resolvedStatus = queryStatus || normalizeOrderStatus(order?.status) || 'unknown';
  const statusConfig = STATUS_CONFIG[resolvedStatus] || STATUS_CONFIG.unknown;
  useEffect(() => { if (resolvedStatus === 'success') trackConfirmedGa4Purchase(); }, [resolvedStatus]);
  const assetItems = useMemo(() => getMarketplaceOrderAssetItems(order), [order]);

  useEffect(() => {
    if (resolvedStatus !== 'success' || cartClearedRef.current) {
      return;
    }

    cartClearedRef.current = true;
    getMarketplaceCartController(window).clearAfterCheckout().catch((error) => {
      console.log('Error while clearing marketplace cart after successful checkout', error);
    });
  }, [resolvedStatus]);

  return (
    <div className="layout marketplace-success-page">
      <Header />
      <main className="marketplace-success">
        <section className="marketplace-success__card">
          <div className={`marketplace-success__status is-${statusConfig.tone}`}>
            <div className="marketplace-success__status-icon">
              <StatusIcon tone={statusConfig.tone} />
            </div>
            <div className="marketplace-success__status-copy">
              <div className="marketplace-success__eyebrow">{statusConfig.eyebrow}</div>
              <h1>{statusConfig.title}</h1>
              <p>{statusConfig.description}</p>
            </div>
            <div className={`marketplace-success__badge is-${statusConfig.tone}`}>
              {statusConfig.badgeLabel}
            </div>
          </div>

          <div className="marketplace-success__meta">
            {orderId &&
            <div className="marketplace-success__meta-item">
              <span className="label">Order</span>
              <span className="value">#{orderId}</span>
            </div>
            }
            <div className="marketplace-success__meta-item">
              <span className="label">Reported status</span>
              <span className="value">{statusConfig.badgeLabel}</span>
            </div>
            {order?.status &&
            <div className="marketplace-success__meta-item">
              <span className="label">Order status</span>
              <span className="value">{getMarketplaceOrderStatusLabel(order.status)}</span>
            </div>
            }
          </div>

          {(isOrderLoading || order || orderError) &&
          <div className="marketplace-success__details">
            <div className="marketplace-success__details-title">Order details</div>

            {isOrderLoading &&
            <div className="marketplace-success__details-note">Loading order details...</div>
            }

            {!isOrderLoading && order &&
            <div className="marketplace-success__details-grid">
              <div className="marketplace-success__details-item">
                <span className="label">Amount</span>
                <span className="value">{formatMarketplacePrice(order.total_amount, order.currency)}</span>
              </div>
              <div className="marketplace-success__details-item">
                <span className="label">Paid at</span>
                <span className="value">{formatMarketplaceDateTime(order.paid_at || order.fulfilled_at)}</span>
              </div>
              {order.provider &&
              <div className="marketplace-success__details-item">
                <span className="label">Provider</span>
                <span className="value">{order.provider}</span>
              </div>
              }
              {assetItems.length > 0 &&
              <div className="marketplace-success__details-item">
                <span className="label">Assets</span>
                <span className="value">{assetItems.length}</span>
              </div>
              }
            </div>
            }

            {!isOrderLoading && !order && orderError &&
            <div className="marketplace-success__details-note is-error">{orderError}</div>
            }
          </div>
          }

          <div className="marketplace-success__actions">
            <Link to="/account/billing" className="marketplace-success__action">
              Go to account
            </Link>
            <Link to="/" className="marketplace-success__action is-secondary">
              Back to plans
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MarketplaceSuccess;
